
import React from 'react';
import { Calendar, User, ArrowLeft, Share2, BookOpen, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import WhatsAppButton from '../WhatsAppButton';
import { Button } from '../ui/button';

interface BlogArticleProps {
  id: string;
  title: string;
  content: string;
  image: string;
  date: string;
  author: string;
  category: string;
  readTime: string;
  excerpt: string;
  tags: string[];
}

const BlogArticle = ({ 
  title, 
  content, 
  image, 
  date, 
  author, 
  category, 
  readTime, 
  excerpt,
  tags 
}: BlogArticleProps) => {
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: title,
        text: excerpt,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-blue-50 font-montserrat">
      <Header />
      
      {/* Article Header */}
      <section className="pt-32 pb-12">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-8">
              <Link to="/" className="hover:text-pink-600 transition-colors">Accueil</Link>
              <span>/</span>
              <Link to="/blog" className="hover:text-pink-600 transition-colors">Blog</Link>
              <span>/</span>
              <span className="text-pink-600">{title}</span>
            </div>

            {/* Back Button */}
            <Link 
              to="/blog" 
              className="inline-flex items-center gap-2 text-pink-600 hover:text-pink-700 mb-8 transition-colors group"
            >
              <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
              Retour au blog
            </Link>

            {/* Category Badge */}
            <div className="mb-6">
              <span className="bg-gradient-to-r from-pink-500 to-blue-500 text-white px-4 py-2 rounded-full text-sm font-medium">
                {category}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              {title}
            </h1>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-8">
              <div className="flex items-center gap-2">
                <User size={16} />
                <span>{author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar size={16} />
                <span>{date}</span>
              </div>
              <div className="flex items-center gap-2">
                <BookOpen size={16} />
                <span>{readTime}</span>
              </div>
              <Button 
                onClick={handleShare}
                variant="outline" 
                size="sm"
                className="flex items-center gap-2"
              >
                <Share2 size={16} />
                Partager
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="pb-12">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="relative overflow-hidden rounded-2xl shadow-2xl">
              <img 
                src={image} 
                alt={title}
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="pb-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <div className="text-xl text-gray-700 leading-relaxed font-light mb-8">
                {excerpt}
              </div>
              
              <div 
                className="text-gray-800 leading-relaxed space-y-6"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            </div>

            {/* Tags */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag, index) => (
                  <span 
                    key={index}
                    className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm hover:bg-pink-100 hover:text-pink-700 transition-colors cursor-pointer"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Author Bio */}
            <div className="mt-12 p-8 bg-gradient-to-r from-pink-50 to-blue-50 rounded-2xl">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 bg-gradient-to-r from-pink-500 to-blue-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  JK
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Julycia Kiyal</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Consultante experte en communication 360° et branding. Passionnée par l'innovation 
                    et la créativité, j'accompagne les entreprises dans leur transformation digitale 
                    et leur stratégie de marque.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="mt-16 text-center p-12 bg-gradient-to-r from-pink-500 to-blue-500 rounded-2xl text-white">
              <Heart className="mx-auto mb-4 text-pink-200" size={48} />
              <h3 className="text-2xl font-bold mb-4">Vous avez aimé cet article ?</h3>
              <p className="text-lg mb-8 opacity-90">
                Discutons de votre projet et voyons comment je peux vous aider à atteindre vos objectifs.
              </p>
              <Link to="/#contact">
                <Button variant="secondary" size="lg" className="bg-white text-pink-600 hover:bg-pink-50">
                  Prendre contact
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default BlogArticle;
